// (B) 플레인 Layout — antd Layout 대체(순수 시각/구조). antd DOM 미러링:
// section.om-layout(+.om-layout-has-sider) / header.om-layout-header / main.om-layout-content / aside.om-layout-sider.
// has-sider(가로 배치)는 antd 처럼 Context 로 Sider 등록 → 부모 Layout 이 감지(Menu 래퍼 너머 중첩 Sider 도 인식).
// Sider breakpoint 는 matchMedia 로 반응형 자동 collapse(antd dimensionMaxMap 재현).
import { createContext, useContext, useEffect, useId, useMemo, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import type {
  OmwLayoutContentProps,
  OmwLayoutHeaderProps,
  OmwLayoutProps,
  OmwLayoutSiderProps,
} from '../api/OmwLayout'
import { cx } from './cx'
import '../styles/omwLayout.css'

interface LayoutCtx {
  addSider: (id: string) => void
  removeSider: (id: string) => void
}

const LayoutContext = createContext<LayoutCtx | null>(null)

// antd Sider breakpoint → max-width 미디어쿼리(v5 dimensionMaxMap).
const BREAKPOINT_MAX: Record<NonNullable<OmwLayoutSiderProps['breakpoint']>, string> = {
  xs: '479.98px',
  sm: '575.98px',
  md: '767.98px',
  lg: '991.98px',
  xl: '1199.98px',
  xxl: '1599.98px',
}

export function PlainLayout({ className, style, children }: OmwLayoutProps) {
  const [siders, setSiders] = useState<string[]>([])
  const ctx = useMemo<LayoutCtx>(
    () => ({
      addSider: (id) => setSiders((prev) => [...prev, id]),
      removeSider: (id) => setSiders((prev) => prev.filter((x) => x !== id)),
    }),
    [],
  )
  const hasSider = siders.length > 0

  return (
    <LayoutContext.Provider value={ctx}>
      <section
        className={cx('om-layout', hasSider && 'om-layout-has-sider', className)}
        style={style}
      >
        {children}
      </section>
    </LayoutContext.Provider>
  )
}

export function PlainLayoutHeader({ className, style, children }: OmwLayoutHeaderProps) {
  return (
    <header className={cx('om-layout-header', className)} style={style}>
      {children}
    </header>
  )
}

export function PlainLayoutContent({ className, style, children }: OmwLayoutContentProps) {
  return (
    <main className={cx('om-layout-content', className)} style={style}>
      {children}
    </main>
  )
}

export function PlainLayoutSider({
  width = 200,
  collapsedWidth = 80,
  collapsible,
  collapsed,
  onCollapse,
  breakpoint,
  className,
  style,
  children,
}: OmwLayoutSiderProps) {
  const ctx = useContext(LayoutContext)
  const id = useId()

  // 부모 Layout 에 Sider 등록(has-sider 감지).
  useEffect(() => {
    ctx?.addSider(id)
    return () => ctx?.removeSider(id)
  }, [ctx, id])

  // breakpoint 반응형 자동 collapse(matchMedia).
  const onCollapseRef = useRef(onCollapse)
  onCollapseRef.current = onCollapse
  useEffect(() => {
    if (breakpoint == null || typeof window.matchMedia !== 'function') return undefined
    const mql = window.matchMedia(`(max-width: ${BREAKPOINT_MAX[breakpoint]})`)
    const handler = (e: MediaQueryListEvent) => onCollapseRef.current?.(e.matches)
    onCollapseRef.current?.(mql.matches)
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  }, [breakpoint])

  const activeWidth = collapsed ? collapsedWidth : width
  const px = typeof activeWidth === 'number' ? `${activeWidth}px` : activeWidth
  const siderStyle: CSSProperties = {
    flex: `0 0 ${px}`,
    maxWidth: px,
    minWidth: px,
    width: px,
    ...style,
  }

  return (
    <aside
      className={cx('om-layout-sider', collapsed && 'om-layout-sider-collapsed', className)}
      style={siderStyle}
    >
      <div className="om-layout-sider-children">{children}</div>
      {collapsible && (
        <div
          className="om-layout-sider-trigger"
          role="button"
          style={{ width: px }}
          onClick={() => onCollapse?.(!collapsed)}
        >
          {collapsed ? '›' : '‹'}
        </div>
      )}
    </aside>
  )
}
